import { zooplaTheme } from '@blueprint/theme';
import { ThemeProvider } from '@blueprint/ui';
import { cache } from '@emotion/css';
import { CacheProvider } from '@emotion/react';
import { globalStyles } from '@zoopla-eng/zds-components/themes';
import { portalLight } from '@zoopla-eng/zds/themes/portal';
import { NudgesTracker } from '@zoopla/nudges-tracker';
import {
    OptimizelyProvider,
    WITH_OPTIMIZELY_PAGE_PROP_NAME,
    WithOptimizelyPageProps
} from '@zoopla/optimizely';
import 'intersection-observer';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import { useEffect } from 'react';
import '../utils/globalThisPolyfill'; // Required for older browsers e.g. Safari < v13
import { handleAuthPagesRouting } from '../utils/handleAuthPagesRouting';
import { handleForSaleLandingRouting } from '../utils/handleForSaleLandingRouting';
import { handleToRentLandingRouting } from '../utils/handleToRentLandingRouting';
import useAnalytics from '../utils/useAnalytics';
import { UserProvider } from '@zoopla/user';

const OptimizelyDevToggles = dynamic<Readonly<Record<string, never>>>(
    () => import('@zoopla/optimizely').then((mod) => mod.DevToggle),
    { ssr: false }
);

interface CustomAppProps extends AppProps<WithOptimizelyPageProps> {
    Component: AppProps<WithOptimizelyPageProps>['Component'];
    pageProps: WithOptimizelyPageProps & { __USER_FORCE_REFRESH__?: boolean };
}

const isDev = process.env.NODE_ENV === 'development';

function AppWrapper({ Component, pageProps }: CustomAppProps) {
    useAnalytics();

    useEffect(() => {
        if (document) {
            document.documentElement.className = 'js';
        }
        NudgesTracker.setSession();
    }, []);

    useEffect(() => {
        if (!isDev) {
            Router.events.on('routeChangeStart', handleAuthPagesRouting);
            Router.events.on('routeChangeStart', handleForSaleLandingRouting);
            Router.events.on('routeChangeStart', handleToRentLandingRouting);

            return () => {
                Router.events.off(
                    'routeChangeComplete',
                    handleAuthPagesRouting
                );

                Router.events.off(
                    'routeChangeComplete',
                    handleForSaleLandingRouting
                );

                Router.events.off(
                    'routeChangeComplete',
                    handleToRentLandingRouting
                );
            };
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [Router.events]);

    return (
        <UserProvider forceRefresh={pageProps?.__USER_FORCE_REFRESH__}>
            <OptimizelyProvider
                state={pageProps[WITH_OPTIMIZELY_PAGE_PROP_NAME['STATE']]}
            >
                <CacheProvider value={cache}>
                    <ThemeProvider theme={zooplaTheme}>
                        {globalStyles()}
                        {isDev && <OptimizelyDevToggles />}
                        <div className={portalLight}>
                            <Component {...pageProps} />
                        </div>
                    </ThemeProvider>
                </CacheProvider>
            </OptimizelyProvider>
        </UserProvider>
    );
}

export default AppWrapper;
